import React, { useState, useRef } from 'react';
import { Card, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { colors } from '../brand-colors';

/**
 * CTA Banner Upload Component
 * Handles uploading a single CTA banner, setting its destination URL, and pushing to WordPress.
 *
 * Props:
 * - bannerType: 'lead_magnet' | 'product'
 * - title: Display title (e.g., "Lead Magnet CTA")
 * - description: Help text
 * - existingBanner: Existing banner data if already uploaded
 * - onBannerUpdated: Callback when banner is created/updated
 * - siteConnected: Whether WordPress site is connected (enables Push to WordPress)
 * - defaultDestinationUrl: Optional default URL to prefill
 */
function CTABannerUpload({ bannerType, title, description, existingBanner, onBannerUpdated, siteConnected = false, defaultDestinationUrl = null }) {
  const [banner, setBanner] = useState(existingBanner || null);
  const [uploading, setUploading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [destinationUrl, setDestinationUrl] = useState(existingBanner?.destination_url || defaultDestinationUrl || '');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const RECOMMENDED_WIDTH = 800;
  const RECOMMENDED_HEIGHT = 150;

  // Update destination URL when defaultDestinationUrl changes (e.g., user selects "keep existing sales page")
  React.useEffect(() => {
    if (defaultDestinationUrl && !destinationUrl && !existingBanner?.destination_url) {
      setDestinationUrl(defaultDestinationUrl);
    }
  }, [defaultDestinationUrl]);

  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a PNG, JPG, GIF, or WebP image.');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    setSelectedFile(file);
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select an image first.');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('name', title);
    formData.append('banner_type', bannerType);
    formData.append('destination_url', destinationUrl);

    try {
      const response = await fetch('/api/cta/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const newBanner = {
          id: data.banner_id,
          temp_url: data.temp_url,
          destination_url: destinationUrl,
          is_pushed: false,
          wp_media_url: null
        };
        setBanner(newBanner);
        setSelectedFile(null);

        if (data.needs_resize) {
          setError(`Note: Your image is ${data.dimensions.width}x${data.dimensions.height}px. Recommended size is ${data.recommended_size}.`);
        }

        // If WordPress is already connected, auto-push immediately
        if (siteConnected) {
          setSuccess('Banner uploaded! Pushing to WordPress...');
          setUploading(false); // End upload state before starting push

          // Auto-push to WordPress
          try {
            setPushing(true);
            const pushResponse = await fetch(`/api/cta/banners/${data.banner_id}/push`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' }
            });

            const pushData = await pushResponse.json();

            if (pushResponse.ok && pushData.success) {
              const pushedBanner = {
                ...newBanner,
                is_pushed: true,
                wp_media_url: pushData.wp_media_url
              };
              setBanner(pushedBanner);
              setSuccess('Banner uploaded and pushed to WordPress!');
              if (onBannerUpdated) {
                onBannerUpdated(pushedBanner);
              }
            } else {
              setSuccess('Banner uploaded! Push to WordPress failed - you can retry manually.');
              if (onBannerUpdated) {
                onBannerUpdated(newBanner);
              }
            }
          } catch (pushErr) {
            console.error('Auto-push error:', pushErr);
            setSuccess('Banner uploaded! Push to WordPress failed - you can retry manually.');
            if (onBannerUpdated) {
              onBannerUpdated(newBanner);
            }
          } finally {
            setPushing(false);
          }
          return; // Exit early since we handled everything
        }

        // WordPress not connected - just show upload success
        setSuccess('Banner uploaded! It will be pushed to WordPress when you connect your site.');
        if (onBannerUpdated) {
          onBannerUpdated(newBanner);
        }
      } else {
        setError(data.error || 'Failed to upload banner.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handlePushToWordPress = async () => {
    if (!banner?.id) {
      setError('No banner to push. Please upload first.');
      return;
    }

    setPushing(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`/api/cta/banners/${banner.id}/push`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const updatedBanner = {
          ...banner,
          is_pushed: true,
          wp_media_url: data.wp_media_url
        };
        setBanner(updatedBanner);
        setSuccess('Banner pushed to WordPress successfully!');

        if (onBannerUpdated) {
          onBannerUpdated(updatedBanner);
        }
      } else {
        setError(data.error || 'Failed to push banner to WordPress.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Push error:', err);
    } finally {
      setPushing(false);
    }
  };

  const handleUpdateDestination = async () => {
    if (!banner?.id) return;

    try {
      const response = await fetch(`/api/cta/banners/${banner.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ destination_url: destinationUrl })
      });

      if (response.ok) {
        setSuccess('Destination URL updated!');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Update error:', err);
    }
  };

  const getStatusBadge = () => {
    if (banner?.is_pushed || banner?.wp_media_url) {
      return <Badge bg="success">Ready</Badge>;
    } else if (banner?.id) {
      return <Badge bg="warning" text="dark">Needs Push</Badge>;
    }
    return <Badge bg="secondary">Not Uploaded</Badge>;
  };

  const getPreviewImage = () => {
    if (previewUrl) return previewUrl;
    if (banner?.wp_media_url) return banner.wp_media_url;
    if (banner?.temp_url) return banner.temp_url;
    return null;
  };

  return (
    <Card className="mb-3" style={{ border: `1px solid ${colors.greyLight}` }}>
      <Card.Header
        className="d-flex justify-content-between align-items-center"
        style={{ backgroundColor: colors.greyLightest }}
      >
        <div>
          <strong>{title}</strong>
          <small className="d-block text-muted">{description}</small>
        </div>
        {getStatusBadge()}
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="warning" className="py-2">{error}</Alert>}
        {success && <Alert variant="success" className="py-2">{success}</Alert>}

        {/* Preview or Upload Area */}
        {getPreviewImage() ? (
          <div className="mb-3">
            <div
              style={{
                border: `2px solid ${colors.greyLight}`,
                borderRadius: '8px',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                textAlign: 'center'
              }}
            >
              <img
                src={getPreviewImage()}
                alt={title}
                style={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  borderRadius: '4px'
                }}
              />
            </div>
            {!banner?.id && (
              <Button
                size="sm"
                variant="link"
                onClick={() => {
                  setPreviewUrl(null);
                  setSelectedFile(null);
                }}
              >
                Choose different image
              </Button>
            )}
          </div>
        ) : (
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragOver ? colors.goldMid : colors.greyMid}`,
              borderRadius: '8px',
              padding: '30px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: dragOver ? '#fff8e6' : '#f8f9fa',
              marginBottom: '15px',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📤</div>
            <p className="mb-1"><strong>Click or drag to upload</strong></p>
            <small className="text-muted">
              Recommended size: {RECOMMENDED_WIDTH}x{RECOMMENDED_HEIGHT}px<br />
              Supported: PNG, JPG, GIF, WebP
            </small>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              style={{ display: 'none' }}
            />
          </div>
        )}

        {/* Destination URL */}
        <Form.Group className="mb-3">
          <Form.Label style={{ fontSize: '0.9rem' }}>
            Landing Page URL
            <small className="text-muted ms-2">(where should the banner link to?)</small>
          </Form.Label>
          <Form.Control
            type="url"
            placeholder="https://yoursite.com/landing-page"
            value={destinationUrl}
            onChange={(e) => setDestinationUrl(e.target.value)}
            onBlur={banner?.id ? handleUpdateDestination : undefined}
          />
        </Form.Group>

        {/* Action Buttons */}
        <div className="d-flex gap-2 flex-wrap">
          {!banner?.id && selectedFile && (
            <Button
              variant="dark"
              onClick={handleUpload}
              disabled={uploading}
              style={{ backgroundColor: colors.greyDarkest }}
            >
              {uploading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Uploading...
                </>
              ) : (
                'Upload Banner'
              )}
            </Button>
          )}

          {banner?.id && !banner?.is_pushed && !banner?.wp_media_url && siteConnected && (
            <Button
              variant="warning"
              onClick={handlePushToWordPress}
              disabled={pushing}
            >
              {pushing ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Pushing to WordPress...
                </>
              ) : (
                'Push to WordPress'
              )}
            </Button>
          )}

          {banner?.id && (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                setBanner(null);
                setPreviewUrl(null);
                setSelectedFile(null);
                setDestinationUrl('');
              }}
            >
              Replace Banner
            </Button>
          )}
        </div>

        {/* Status indicator */}
        {banner?.wp_media_url && (
          <div className="mt-3 text-success" style={{ fontSize: '0.85rem' }}>
            <strong>✓ Ready to use!</strong> This banner will appear on your blog posts.
          </div>
        )}
        {banner?.id && !banner?.wp_media_url && !siteConnected && (
          <div className="mt-3 text-muted" style={{ fontSize: '0.85rem' }}>
            <strong>Uploaded!</strong> Connect your WordPress site to push this banner and use it on blog posts.
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default CTABannerUpload;
